package com.iptvplayer.shared.presentation.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.iptvplayer.shared.domain.model.Channel
import com.iptvplayer.shared.domain.model.SeriesItem
import com.iptvplayer.shared.domain.model.SyncReport
import com.iptvplayer.shared.domain.model.VodItem
import com.iptvplayer.shared.domain.repository.ContentRepository
import com.iptvplayer.shared.presentation.state.ContentIssue
import com.iptvplayer.shared.presentation.state.toContentIssue
import com.iptvplayer.shared.util.Result
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

/**
 * Browse, paged straight from the local DB.
 *
 * Unlike [BrowseViewModel] (which holds the whole catalog in RAM and filters it
 * in Kotlin on every keystroke), this VM never materializes the full lists. Each
 * content type exposes a *windowed* flow: the UI loads one growing window
 * (`limit`) at a time, and category-filtering + search both happen in SQL,
 * returning only the rows on screen.
 *
 * Perf-critical invariants:
 * - Only the **active type's** window/total queries actually run. The other two
 *   types' flows return [emptyList]/0 until the user switches tabs, so a single
 *   keystroke fires at most one window + one count, not three of each.
 * - Tab badge counts are **catalog totals** (ignore the search filter), so
 *   they only re-emit when content changes, not on every search keystroke.
 */
@OptIn(ExperimentalCoroutinesApi::class, FlowPreview::class)
class BrowsePagingViewModel(
    private val repository: ContentRepository
) : ViewModel() {

    enum class BrowseType { LIVE, MOVIES, SERIES }

    private val pageSize = PAGE_SIZE

    private val _searchInput = MutableStateFlow("")
    /** Immediate text-field value (debounced separately before hitting the DB). */
    val searchInput: StateFlow<String> = _searchInput.asStateFlow()

    private val _selectedType = MutableStateFlow(BrowseType.LIVE)
    val selectedType: StateFlow<BrowseType> = _selectedType.asStateFlow()

    private val _selectedCategory = MutableStateFlow<String?>(null)
    val selectedCategory: StateFlow<String?> = _selectedCategory.asStateFlow()

    /** Debounced, blank-normalized query that actually drives the SQL filter. */
    private val query: StateFlow<String?> = _searchInput
        .debounce(SEARCH_DEBOUNCE_MS)
        .map { it.trim().ifBlank { null } }
        .distinctUntilChanged()
        .stateIn(viewModelScope, SharingStarted.Eagerly, null)

    // Deep pages fetched one-shot and appended below the reactive first window.
    // Growing a single reactive LIMIT re-read and re-mapped the WHOLE window on
    // every page (O(n²) across a long scroll through a 20k catalog); appending
    // static pages makes each "load more" cost exactly one 60-row query.
    private val channelPages = MutableStateFlow<List<Channel>>(emptyList())
    private val vodPages = MutableStateFlow<List<VodItem>>(emptyList())
    private val seriesPages = MutableStateFlow<List<SeriesItem>>(emptyList())
    private val loadingMore = mutableSetOf<BrowseType>()

    private val _isSyncing = MutableStateFlow(false)
    /** True while the initial sync / forced refresh is in flight — lets the UI
     * show a spinner instead of a misleading empty state. */
    val isSyncing: StateFlow<Boolean> = _isSyncing.asStateFlow()

    private val _issue = MutableStateFlow<ContentIssue?>(null)
    /** Why content may be missing (no sources / provider down / …) — see [ContentIssue]. */
    val issue: StateFlow<ContentIssue?> = _issue.asStateFlow()

    /** What the sync is doing right now (source / series progress), null when idle. */
    val syncStatus: StateFlow<String?> = repository.syncStatus
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(STOP_TIMEOUT_MS), null)

    init {
        // Any filter change resets every window back to the first page.
        viewModelScope.launch {
            combine(_selectedCategory, query) { _, _ -> Unit }
                .collect {
                    channelPages.value = emptyList()
                    vodPages.value = emptyList()
                    seriesPages.value = emptyList()
                }
        }
        // Make sure content is actually in the DB (Home normally triggers this;
        // calling again is a cheap no-op once synced) and pull the series catalog
        // once per session.
        viewModelScope.launch {
            val result = runSync { repository.syncSources() }
            // Only chase the series catalog when there is a source to fetch from
            // (a `first { isNotEmpty }` wait here would hang forever otherwise).
            if ((result.getOrNull()?.sourceCount ?: 0) > 0) {
                repository.ensureSeriesLoaded()
            }
        }
    }

    /** Run a sync with the spinner up, and translate its outcome for the UI. */
    private suspend fun runSync(sync: suspend () -> Result<SyncReport>): Result<SyncReport> {
        _isSyncing.value = true
        val result = try {
            sync()
        } finally {
            _isSyncing.value = false
        }
        _issue.value = when (result) {
            is Result.Success -> result.data.toContentIssue()
            is Result.Error -> ContentIssue.SyncFailed(result.message)
            else -> null
        }
        return result
    }

    fun dismissIssue() {
        _issue.value = null
    }

    // ──────────────────── Windowed content (active-type only) ────────────────────
    // Each window/total flow is gated by [_selectedType]: when the user is on
    // SERIES, the LIVE and MOVIES flows emit empty/0 instead of running SQL.
    // This is what cuts per-keystroke work from 6 queries to 2.

    val channels: StateFlow<List<Channel>> =
        combine(_selectedType, _selectedCategory, query) { t, c, q -> Triple(t, c, q) }
            .flatMapLatest { (t, c, q) ->
                if (t == BrowseType.LIVE) repository.browseChannels(c, q, pageSize) else flowOf(emptyList())
            }
            .combine(channelPages) { window, extra ->
                // distinctBy: a sync write can shift rows between the reactive
                // window and an already-fetched page; duplicate keys would
                // crash the lazy grid.
                if (extra.isEmpty()) window else (window + extra).distinctBy { it.id }
            }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(STOP_TIMEOUT_MS), emptyList())

    val channelTotal: StateFlow<Long> =
        combine(_selectedType, _selectedCategory, query) { t, c, q -> Triple(t, c, q) }
            .flatMapLatest { (t, c, q) ->
                if (t == BrowseType.LIVE) repository.browseChannelCount(c, q) else flowOf(0L)
            }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(STOP_TIMEOUT_MS), 0L)

    val vod: StateFlow<List<VodItem>> =
        combine(_selectedType, _selectedCategory, query) { t, c, q -> Triple(t, c, q) }
            .flatMapLatest { (t, c, q) ->
                if (t == BrowseType.MOVIES) repository.browseVod(c, q, pageSize) else flowOf(emptyList())
            }
            .combine(vodPages) { window, extra ->
                if (extra.isEmpty()) window else (window + extra).distinctBy { it.id }
            }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(STOP_TIMEOUT_MS), emptyList())

    val vodTotal: StateFlow<Long> =
        combine(_selectedType, _selectedCategory, query) { t, c, q -> Triple(t, c, q) }
            .flatMapLatest { (t, c, q) ->
                if (t == BrowseType.MOVIES) repository.browseVodCount(c, q) else flowOf(0L)
            }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(STOP_TIMEOUT_MS), 0L)

    val series: StateFlow<List<SeriesItem>> =
        combine(_selectedType, _selectedCategory, query) { t, c, q -> Triple(t, c, q) }
            .flatMapLatest { (t, c, q) ->
                if (t == BrowseType.SERIES) repository.browseSeries(c, q, pageSize) else flowOf(emptyList())
            }
            .combine(seriesPages) { window, extra ->
                if (extra.isEmpty()) window else (window + extra).distinctBy { it.id }
            }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(STOP_TIMEOUT_MS), emptyList())

    val seriesTotal: StateFlow<Long> =
        combine(_selectedType, _selectedCategory, query) { t, c, q -> Triple(t, c, q) }
            .flatMapLatest { (t, c, q) ->
                if (t == BrowseType.SERIES) repository.browseSeriesCount(c, q) else flowOf(0L)
            }
            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(STOP_TIMEOUT_MS), 0L)

    // Tab badge counts are the *catalog totals* (no search filter), so the
    // badge is stable across keystrokes — typing only requeries the active
    // tab's filtered total above.
    val liveCount: StateFlow<Long> = repository.browseChannelCount(null, null)
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(STOP_TIMEOUT_MS), 0L)

    val moviesCount: StateFlow<Long> = repository.browseVodCount(null, null)
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(STOP_TIMEOUT_MS), 0L)

    val seriesCount: StateFlow<Long> = repository.browseSeriesCount(null, null)
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(STOP_TIMEOUT_MS), 0L)

    // ──────────────────── Categories (per type, from DB) ────────────────────

    val channelCategories: StateFlow<List<String>> = repository.channelGroups()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(STOP_TIMEOUT_MS), emptyList())

    val vodCategories: StateFlow<List<String>> = repository.vodGroups()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(STOP_TIMEOUT_MS), emptyList())

    val seriesCategories: StateFlow<List<String>> = repository.seriesGroups()
        .stateIn(viewModelScope, SharingStarted.WhileSubscribed(STOP_TIMEOUT_MS), emptyList())

    // ──────────────────── Intents ────────────────────

    fun setSearch(value: String) {
        _searchInput.value = value
    }

    fun selectCategory(category: String?) {
        _selectedCategory.value = category
    }

    fun selectType(type: BrowseType) {
        if (_selectedType.value != type) {
            _selectedType.value = type
            // Categories differ per type, so a stale selection wouldn't apply.
            _selectedCategory.value = null
            _searchInput.value = ""
            channelPages.value = emptyList()
            vodPages.value = emptyList()
            seriesPages.value = emptyList()
        }
    }

    fun loadMoreChannels() = loadMore(BrowseType.LIVE, channelPages, channelTotal) { limit, offset ->
        repository.pageChannels(_selectedCategory.value, query.value, limit, offset)
    }

    fun loadMoreVod() = loadMore(BrowseType.MOVIES, vodPages, vodTotal) { limit, offset ->
        repository.pageVod(_selectedCategory.value, query.value, limit, offset)
    }

    fun loadMoreSeries() = loadMore(BrowseType.SERIES, seriesPages, seriesTotal) { limit, offset ->
        repository.pageSeries(_selectedCategory.value, query.value, limit, offset)
    }

    /** Fetch the next static page (one 60-row query) and append it. */
    private fun <T> loadMore(
        type: BrowseType,
        pages: MutableStateFlow<List<T>>,
        total: StateFlow<Long>,
        fetch: suspend (limit: Long, offset: Long) -> List<T>
    ) {
        val loaded = pageSize + pages.value.size
        if (loaded >= total.value || type in loadingMore) return
        loadingMore += type
        viewModelScope.launch {
            try {
                val page = fetch(pageSize, loaded)
                if (page.isNotEmpty()) pages.value = pages.value + page
            } finally {
                loadingMore -= type
            }
        }
    }

    /** Force a full re-sync of live + VOD and re-pull the series catalog. */
    fun refresh() {
        viewModelScope.launch {
            val result = runSync { repository.syncSources(force = true) }
            if ((result.getOrNull()?.sourceCount ?: 0) > 0) {
                repository.refreshSeries()
            }
        }
    }

    private companion object {
        const val PAGE_SIZE = 60L
        const val SEARCH_DEBOUNCE_MS = 300L
        const val STOP_TIMEOUT_MS = 5_000L
    }
}
